import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appVersionAPI } from '../../services/api';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { inDateRange, matchesSearch, formatExportDate } from '../../utils/exportData';
import {
  HiOutlineCloudUpload,
  HiOutlineMail,
  HiOutlineTrash,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineDocument,
  HiOutlineDownload,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

// LOCAL: matches backend .env PORT=5001
const APP_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

// ─── File Size Formatter ──────────────────────────────────────────────────
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

// ─── Component ────────────────────────────────────────────────────────────
const EMPTY_VERSION_FILTERS = { search: '', status: '', notified: '', from: '', to: '' };

const VERSION_EXPORT_COLUMNS = [
  { header: 'Version', value: (v) => v.version },
  { header: 'Version Code', value: (v) => v.versionCode },
  { header: 'File Name', value: (v) => v.fileName },
  { header: 'File Size (MB)', value: (v) => (v.fileSize ? (v.fileSize / (1024 * 1024)).toFixed(2) : '') },
  { header: 'Changelog', value: (v) => v.changelog },
  { header: 'Status', value: (v) => (v.isActive ? 'Active' : 'Inactive') },
  { header: 'Notification Sent', value: (v) => !!v.notificationSent },
  { header: 'Notified At', value: (v) => formatExportDate(v.notificationSentAt) },
  { header: 'Uploaded By', value: (v) => v.uploadedBy?.name || 'SuperAdmin' },
  { header: 'Uploaded', value: (v) => formatExportDate(v.createdAt) },
];

const AppVersions = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [notifying, setNotifying] = useState(null); // version ID being notified
  const [deleting, setDeleting] = useState(null); // version ID being deleted
  const [formData, setFormData] = useState({
    version: '',
    versionCode: '',
    changelog: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  // Fetch versions
  const { data: versionsData, isLoading, refetch } = useQuery({
    queryKey: ['app-versions'],
    queryFn: async () => {
      const res = await appVersionAPI.getVersions({ limit: 500 });
      return res.data;
    },
  });

  const allVersions = versionsData?.data || [];
  const pagination = versionsData?.pagination || {};
  const [versionFilters, setVersionFilters] = useState(EMPTY_VERSION_FILTERS);
  const setVersionFilter = (patch) => setVersionFilters((prev) => ({ ...prev, ...patch }));
  const versionFiltersActive = Object.keys(EMPTY_VERSION_FILTERS).some((k) => versionFilters[k]);
  const versions = allVersions.filter((v) =>
    matchesSearch(versionFilters.search, v.version, v.versionCode, v.fileName, v.changelog)
    && (!versionFilters.status || (versionFilters.status === 'active' ? v.isActive : !v.isActive))
    && (!versionFilters.notified || (versionFilters.notified === 'yes' ? v.notificationSent : !v.notificationSent))
    && inDateRange(v.createdAt, versionFilters.from, versionFilters.to));

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!formData.version) {
      toast.error('Please enter a version number');
      return;
    }

    if (!/^\d+\.\d+(\.\d+)?$/.test(formData.version)) {
      toast.error('Version must be in format X.Y.Z (e.g., 1.2.3)');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select an APK file to upload');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      uploadData.append('version', formData.version);
      if (formData.versionCode) {
        uploadData.append('versionCode', formData.versionCode);
      }
      uploadData.append('changelog', formData.changelog);

      await appVersionAPI.uploadVersion(uploadData);
      toast.success(`Version ${formData.version} uploaded successfully!`);
      setFormData({ version: '', versionCode: '', changelog: '' });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetch();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.message || 'Failed to upload version');
    } finally {
      setUploading(false);
    }
  };

  const handleNotify = async (id) => {
    if (!window.confirm('This will send an email notification to all organization admins about this update. Emails are sent in the background, so this action is quick. Continue?')) return;

    setNotifying(id);
    try {
      const res = await appVersionAPI.notifyVersion(id);
      toast.success(res.data?.message || 'Notifications triggered successfully');
      refetch();
    } catch (err) {
      console.error('Notify error:', err);
      toast.error(err.response?.data?.message || 'Failed to send notifications');
    } finally {
      setNotifying(null);
    }
  };

  const handleDelete = async (id, version) => {
    if (!window.confirm(`Are you sure you want to delete version ${version}? This will also remove the APK file.`)) return;

    setDeleting(id);
    try {
      await appVersionAPI.deleteVersion(id);
      toast.success(`Version ${version} deleted`);
      refetch();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.message || 'Failed to delete version');
    } finally {
      setDeleting(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
            <HiOutlineCloudUpload className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">App Versions</h2>
            <p className="text-sm text-gray-500">Upload and manage telecaller app releases</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 rounded-lg hover:bg-secondary-100 text-secondary-500 hover:text-secondary-700 transition-colors"
          title="Refresh"
        >
          <HiOutlineRefresh className="w-5 h-5" />
        </button>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-gradient-to-r from-primary-50 to-white">
          <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
            <HiOutlineCloudUpload className="w-5 h-5 text-primary-500" />
            Upload New Version
          </h3>
        </div>
        <form onSubmit={handleUpload} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                Version Number <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData((prev) => ({ ...prev, version: e.target.value }))}
                placeholder="e.g., 1.2.3"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
              <p className="text-xs text-secondary-400 mt-1">Format: X.Y.Z (e.g., 1.2.3)</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                Version Code <span className="text-secondary-400 font-normal">(optional)</span>
              </label>
              <input
                type="number"
                value={formData.versionCode}
                onChange={(e) => setFormData((prev) => ({ ...prev, versionCode: e.target.value }))}
                placeholder="Auto-generated from version"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
              <p className="text-xs text-secondary-400 mt-1">Integer for comparison. Auto-calculated if blank (1.2.3 → 10203)</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
              Changelog / What's New
            </label>
            <textarea
              value={formData.changelog}
              onChange={(e) => setFormData((prev) => ({ ...prev, changelog: e.target.value }))}
              placeholder="• Bug fixes and performance improvements&#10;• New feature: Call targets&#10;• UI improvements"
              rows={3}
              className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200 resize-none"
            />
            <p className="text-xs text-secondary-400 mt-1">One feature per line. Sent in the notification email.</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
              APK File <span className="text-danger-500">*</span>
            </label>
            <div className="mt-1 flex items-center gap-4">
              <label className="flex-1 cursor-pointer">
                <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed transition-all duration-200 ${
                  selectedFile ? 'border-primary-300 bg-primary-50' : 'border-secondary-200 bg-white hover:border-primary-300 hover:bg-primary-50/50'
                }`}>
                  <HiOutlineDocument className={`w-5 h-5 flex-shrink-0 ${selectedFile ? 'text-primary-500' : 'text-secondary-400'}`} />
                  <div className="min-w-0 flex-1">
                    {selectedFile ? (
                      <>
                        <p className="text-sm font-medium text-primary-700 truncate">{selectedFile.name}</p>
                        <p className="text-xs text-primary-500">{formatFileSize(selectedFile.size)}</p>
                      </>
                    ) : (
                      <p className="text-sm text-secondary-500">Click to select APK file (max 200MB)</p>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".apk,.aab"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
              {selectedFile && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="p-2 rounded-lg hover:bg-red-50 text-secondary-400 hover:text-red-500 transition-colors"
                  title="Remove file"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={uploading}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 text-white font-medium text-sm hover:bg-primary-700 focus:ring-4 focus:ring-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <HiOutlineCloudUpload className="w-4 h-4" />
                  Upload Version
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Version List */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 space-y-3">
          <h3 className="font-semibold text-secondary-900">
            All Versions ({versionFiltersActive ? `${versions.length} of ${allVersions.length}` : pagination.total || allVersions.length})
          </h3>
          {allVersions.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Search version, file, changelog..."
                value={versionFilters.search}
                onChange={(e) => setVersionFilter({ search: e.target.value })}
                className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
              <FilterSelect value={versionFilters.status} onChange={(v) => setVersionFilter({ status: v })}
                allLabel="All Status" options={[['active', 'Active'], ['inactive', 'Inactive']]} />
              <FilterSelect value={versionFilters.notified} onChange={(v) => setVersionFilter({ notified: v })}
                allLabel="Notified: Any" options={[['yes', 'Notification sent'], ['no', 'Not sent']]} />
              <DateRangeFilter label="Uploaded" from={versionFilters.from} to={versionFilters.to}
                onChange={({ from, to }) => setVersionFilter({ from, to })} />
              <ResetFiltersButton active={versionFiltersActive} onReset={() => setVersionFilters(EMPTY_VERSION_FILTERS)} />
              <ExportMenu fileName="app-versions" columns={VERSION_EXPORT_COLUMNS} getRows={() => versions} />
            </div>
          )}
        </div>

        {allVersions.length > 0 && versions.length === 0 ? (
          <p className="text-center text-sm text-secondary-400 py-12">No versions match these filters</p>
        ) : versions.length === 0 ? (
          <div className="text-center py-12">
            <HiOutlineDocument className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
            <p className="text-sm text-secondary-400 mb-1">No app versions uploaded yet</p>
            <p className="text-xs text-secondary-400">Upload your first APK above to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-100 bg-secondary-50/50">
                  <th className="text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider py-3 px-4">Version</th>
                  <th className="text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider py-3 px-4">File</th>
                  <th className="text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider py-3 px-4">Changelog</th>
                  <th className="text-center text-xs font-semibold text-secondary-500 uppercase tracking-wider py-3 px-4">Status</th>
                  <th className="text-center text-xs font-semibold text-secondary-500 uppercase tracking-wider py-3 px-4">Notified</th>
                  <th className="text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider py-3 px-4">Uploaded</th>
                  <th className="text-center text-xs font-semibold text-secondary-500 uppercase tracking-wider py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v._id} className="border-b border-secondary-50 hover:bg-primary-50/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-secondary-900">v{v.version}</span>
                        <span className="text-xs text-secondary-400">({v.versionCode})</span>
                        {v.isActive && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Latest
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-secondary-700">{v.fileName}</div>
                      <div className="text-xs text-secondary-400">{formatFileSize(v.fileSize)}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-secondary-600 max-w-xs truncate">
                        {v.changelog || '—'}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {v.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <HiOutlineCheckCircle className="w-3.5 h-3.5" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-600">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {v.notificationSent ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
                          <HiOutlineCheckCircle className="w-3.5 h-3.5" />
                          {v.notificationSentAt ? new Date(v.notificationSentAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Yes'}
                        </span>
                      ) : (
                        <span className="text-xs text-secondary-400">Not sent</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-secondary-600">{new Date(v.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      <div className="text-xs text-secondary-400">{v.uploadedBy?.name || 'SuperAdmin'}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleNotify(v._id)}
                          disabled={notifying === v._id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Send update notification to all org admins"
                        >
                          {notifying === v._id ? (
                            <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <HiOutlineMail className="w-3.5 h-3.5" />
                          )}
                          Notify
                        </button>
                        <button
                          onClick={() => handleDelete(v._id, v.version)}
                          disabled={deleting === v._id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete this version"
                        >
                          {deleting === v._id ? (
                            <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <HiOutlineTrash className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
        <p className="text-sm text-primary-700">
          <strong>How it works:</strong> Upload the latest telecaller app APK here. When you click <strong>Notify</strong>, an email will be sent to all organization admins with a download link and changelog. The mobile app can check <code className="bg-primary-100 px-1 rounded">/api/app-version/latest</code> for update info.
        </p>
      </div>
    </div>
  );
};

export default AppVersions;